import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoShopifyCountryCode } from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import { FulfillmentOrderAddress } from "@redotech/redo-model/fulfillments/fulfillment-order-address";
import {
  AddressAutocompleteActions,
  AddressAutocompleteFields,
  AddressAutocompleteFormProps,
  AddressAutocompleteForm as WebAddressForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

export const useAddressAutocompleteActions = (): AddressAutocompleteActions => {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const outboundLabelsRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const getAddressSuggestionsFromAddressLine = useHandler(
    async (query: string, sessionToken?: string, countryCode?: string) => {
      const { suggestions } =
        await merchantRpcClient.getAutocompleteSuggestions({
          query,
          sessionToken,
          countryCode,
        });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion.keyToFullObject,
        displayText: suggestion.displayText,
      }));
    },
  );

  const getAddressFromAddressLineSuggestion = useHandler(
    async (suggestionId: string) => {
      const { address } = await merchantRpcClient.getAddressFromSuggestion({
        suggestionId,
      });
      return address as AddressAutocompleteFields | null;
    },
  );

  const getAddressSuggestionsFromName = useHandler(
    async (nameSearchString: string) => {
      const { suggestions } =
        await outboundLabelsRpcClient.getAddressesFromName({
          nameSearchString,
        });
      return suggestions.map((suggestion) => ({
        keyToFullObject: suggestion,
        displayText: suggestion.name,
      }));
    },
  );

  const getAddressFromNameSuggestion = useHandler(
    async (destinationAddress: FulfillmentOrderAddress) => {
      return {
        name: destinationAddress.name ?? "",
        address1: destinationAddress.address1 ?? "",
        address2: destinationAddress.address2 ?? "",
        city: destinationAddress.city ?? "",
        province: destinationAddress.province ?? "",
        zip: destinationAddress.zip ?? "",
        countryCode: destinationAddress.countryCode as RedoShopifyCountryCode,
        email: destinationAddress.email,
        company: destinationAddress.company,
        phone: destinationAddress.phone,
      };
    },
  );

  return {
    getAddressSuggestionsFromAddressLine,
    getAddressFromAddressLineSuggestion,
    getAddressSuggestionsFromName,
    getAddressFromNameSuggestion,
  };
};

export const AddressForm = memo(function AddressForm({
  form,
  title = "Recipient information",
  hideEmailField = false,
  hideCompanyField = false,
  includeNameField = true,
  autoCompleteOnName = true,
  requirePhone = true,
}: AddressAutocompleteFormProps) {
  const actions = useAddressAutocompleteActions();

  return (
    <WebAddressForm
      actions={actions}
      autoCompleteOnName={autoCompleteOnName}
      form={form}
      hideCompanyField={hideCompanyField}
      hideEmailField={hideEmailField}
      includeNameField={includeNameField}
      requirePhone={requirePhone}
      title={title}
    />
  );
});
